<template>
  <FormContainer
    collection="requisitions"
    :edit="edit"
    title="Requisition"
    :saving="saving"
    :mode="mode"
    :historyRoute="`/requisitions/${id}/history`"

    :docID="id"

    @save="save"
    @cancel="cancel"
    @confirmDelete="onConfirmDelete"

    :createdBy="createdBy"
    :createdByDate="createdByDate"
    :updatedBy="updatedBy"
    :updatedByDate="updatedByDate"
    :fullscreen="fullscreen"
    :denseNotes="true"
    >
    <v-container
      slot="body"
      fluid
      grid-list-md
      :class="[
        'pt-0 pb-0',
        $vuetify.breakpoint.xsOnly && 'pl-2'
      ]"
      :style="{ boxSizing: 'border-box' }"
      >
      <v-layout row wrap>
        <!-- Created By -->
        <template v-if="createdBy">
          <v-flex xs7 sm6>
            <v-text-field
              class="input--dense"
              label="Created By"
              v-model="createdBy.displayName"
              readonly
            ></v-text-field>
          </v-flex>
        </template>

        <!-- Date Created -->
        <v-flex xs5 sm6>
          <v-text-field
            class="input--dense"
            label="Date Created"
            v-model="dateCreated"
            readonly
          ></v-text-field>
        </v-flex>

        <!-- Job -->
        <v-flex xs12 md5 lg4>
          <v-autocomplete
            label="Job"
            v-model="job"
            required
            clearable
            return-object
            :items="jobs"
            :loading="!jobsInit"
            :filter="jobFilter"
            :error-messages="jobErrors"
            @input="onJobInput($event)"
            @blur="onJobBlur($event)"
            @keyup.delete="job = null"
            item-text="job"
            class="autocomplete--nowrap input--dense"
            v-select-width="{
              items: jobs,
              itemValue: '${ number } - ${ name }',
              itemValueIsTemplate: true
            }"
          >

          <template slot="item" slot-scope="data">
            <v-list-tile-content>
              <v-list-tile-title>{{ `${data.item.number} - ${data.item.name}` }}</v-list-tile-title>
            </v-list-tile-content>
          </template>
          <template slot="no-data">
            <template>
              <v-list dense avatar class="py-0" :ripple="false">
                <v-list-tile>
                  <v-list-tile-avatar>
                    <v-icon>mdi-information-outline</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="grey--text">No Jobs match that search</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </template>
          </template>
         </v-autocomplete>
        </v-flex>

        <!-- Shop Drawing -->
        <v-flex xs12 sm9 md5 lg6>
          <v-autocomplete
            label="Shop Drawing"
            v-model="shopDrawing"
            :disabled="!hasJob"
            :items="shopDrawingsList"
            :filter="shopDrawingsFilter"
            :loading="!shopDrawingsInit"
            clearable
            required
            return-object
            dense
            :error-messages="shopDrawingErrors"
            :messages="hasJob ? undefined : ['Select a Job']"
            @input="onShopDrawingInput($event)"
            @blur="onShopDrawingBlur($event)"
            @keyup.delete="shopDrawing = null"
            item-text="shopDrawing"
            class="shop-drawing__select autocomplete--nowrap input--dense"
            ref="shopDrawingInput"
            @focus="onShopDrawingFocus"
            v-select-width="{
              items: shopDrawingsList,
              itemValue: '${ number } - ${ subject }',
              itemValueIsTemplate: true
              }"
          >
          <template slot="item" slot-scope="data">
            <v-list-tile-content>
              <v-list-tile-title>{{ `${data.item.number} - ${data.item.subject}` }}</v-list-tile-title>
            </v-list-tile-content>
          </template>
          <template slot="no-data">
            <template>
              <v-list dense avatar class="py-0" :ripple="false">
                <v-list-tile>
                  <v-list-tile-avatar>
                    <v-icon>mdi-information-outline</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="grey--text">No Shop Drawings match that search</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </template>
          </template>
         </v-autocomplete>
        </v-flex>

        <!-- Work Release -->
        <v-flex xs6 sm3 md2 xlg1>
          <v-autocomplete
            label="WR"
            v-model="workRelease"
            :disabled="!hasShopDrawing"
            :items="workReleasesList"
            :filter="workReleasesFilter"
            :loading="!workReleasesInit"
            clearable
            required
            dense
            return-object
            :error-messages="workReleaseErrors"
            :messages="hasShopDrawing ? undefined : ['Select a SD']"
            @input="onWorkReleaseInput($event)"
            @blur="onWorkReleaseBlur($event)"
            @keyup.delete="workRelease = null"
            item-text="workRelease"
            class="work-release__select autocomplete--nowrap input--dense"
            ref="workReleaseInput"
            @focus="onWorkReleaseFocus"
            v-select-width="{
              items: workReleasesList,
              itemValue: '${ number } - ${ subject }',
              itemValueIsTemplate: true
              }"
          >
          <template
            slot="selection"
            slot-scope="{ item, selected }"
          >
            <span v-text="item.number"></span>
          </template>

          <template slot="item" slot-scope="data">
            <v-list-tile-content>
              <v-list-tile-title>{{ `${data.item.number} - ${data.item.subject}` }}</v-list-tile-title>
            </v-list-tile-content>
          </template>

          <template slot="no-data">
            <template>
              <v-list dense avatar class="py-0" :ripple="false">
                <v-list-tile>
                  <v-list-tile-avatar>
                    <v-icon>mdi-information-outline</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="grey--text">No Work Releases match that search</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </template>
          </template>
         </v-autocomplete>
        </v-flex>

        <!-- Number -->
        <v-flex xs6 sm2 lg1>
          <v-text-field
            class="input--to-number input--dense"
            label="TO No."
            v-model.trim="number"
            :disabled="!hasShopDrawing"
            required
            :error-messages="numberErrors"
            @input="delayTouch($v.number)"
            @blur="delayTouch($v.number)"
            append-icon="mdi-refresh"
            @click:append="refreshTONumber"
            :messages="hasShopDrawing ? undefined : ['Select a Shop Drawing']"
            >
            <span slot="prepend-inner" :style="{ paddingTop: '4px', marginRight: '-2px' }" class="number__prefix">{{ TONumberPrefix }}</span>
          </v-text-field>
        </v-flex>

        <!-- Subject -->
        <v-flex xs12 sm10 lg11>
          <v-text-field
            label="Subject"
            class="input--dense"
            v-model.trim="subject"
            :error-messages="subjectErrors"
            @input="delayTouch($v.subject)"
            @blur="delayTouch($v.subject)"
            ></v-text-field>
        </v-flex>
      </v-layout>
    </v-container>
  </FormContainer>

</template>

<script>
import { clone, cloneDeep, get, getOr, has, isEqual, isNil, maxBy, pick, size, template, toNumber } from 'lodash/fp'
import moment from 'moment'

import { mapGetters, mapActions } from 'vuex'
import { required } from 'vuelidate/lib/validators'

import FormContainer from '@/components/Form/FormContainer'

const touchMap = new WeakMap()

export default {
  name: 'RequisitionForm',
  components: {
    FormContainer
  },
  props: ['edit', 'id', 'mode', 'fullscreen'],
  data () {
    return {
      requisition: null,
      saving: false,

      job: {},

      shopDrawing: {},
      shopDrawingsList: [],

      workRelease: {},
      workReleasesList: [],

      number: null,
      subject: null,
      updateComparisonProperties: [ 'job', 'jobID', 'shopDrawing', 'shopDrawingID', 'subject' ]
    }
  },
  validations () {
    return {
      createdBy: {},
      dateCreated: {},
      job: {
        required
      },
      shopDrawing: {
        required
      },
      number: {
        required
      },
      workRelease: {},
      subject: {}
    }
  },

  computed: {
    maxTONumber () {
      return get('maxTONumber', this.shopDrawing) || 0
    },
    TONumberPrefix () {
      return this.shopDrawing && this.shopDrawing.number
        ? get('number', this.shopDrawing) + '.'
        : null
    },
    createdBy () {
      return this.requisition && this.edit
        ? this.requisition.createdBy
        : clone(this.user)
    },
    dateCreated () {
      return moment(this.createdByDate).format('MMM DD')
    },
    createdByDate () {
      return this.requisition && this.edit
        ? this.requisition.createdByDate
        : new Date()
    },
    updatedBy () { return this.requisition && this.edit ? this.requisition.updatedBy : null },
    updatedByDate () { return this.requisition && this.edit ? this.requisition.updatedByDate : null },
    jobErrors () {
      const errors = []
      if (!this.$v.job.$dirty) return errors
      !this.$v.job.required && errors.push('A Job is required')
      return errors
    },
    hasJob () {
      return this.job && this.job.id
    },

    shopDrawingErrors () {
      const errors = []
      if (!this.$v.shopDrawing.$dirty || isNil(this.job)) return errors
      !this.$v.shopDrawing.required && errors.push('A Shop Drawing is required')
      return errors
    },
    hasShopDrawing () {
      return this.shopDrawing && this.shopDrawing.id
    },

    workReleaseErrors () {
      const errors = []
      if (!this.$v.shopDrawing.$dirty || isNil(this.job)) return errors
      !this.$v.shopDrawing.required && errors.push('A Shop Drawing is required')
      return errors
    },
    hasWorkRelease () {
      return this.workRelease && this.workRelease.id
    },

    numberErrors () {
      const errors = []
      if (!this.$v.number.$dirty || isNil(this.shopDrawing)) return errors
      !this.$v.number.required && errors.push('A TO Number is required')

      return errors
    },

    subjectErrors () {
      const errors = []
      if (!this.$v.subject.$dirty) return errors
      return errors
    },

    ...mapGetters({
      dark: 'user/theme',
      jobs: 'jobs/compactJobs',
      jobsInit: 'jobs/init',
      getJobByQuickbaseRID: 'jobs/getJobByQuickbaseRID',
      shopDrawings: 'shopDrawings/compactShopDrawings',
      user: 'user/saveUser',
      shopDrawingsForJob: 'shopDrawings/shopDrawingsForJob',
      getShopDrawingByQuickbaseRID: 'shopDrawings/getShopDrawingByQuickbaseRID',
      workReleases: 'workReleases/compactWorkReleases',
      workReleasesInit: 'workReleases/init',
      workReleasesForShopDrawing: 'workReleases/workReleasesForShopDrawing',
      getWorkReleaseByQuickbaseRID: 'workReleases/getWorkReleaseByQuickbaseRID',
      shopDrawingsInit: 'shopDrawings/init',
      getRequisition: 'requisitions/requisition',
      currentRequisition: 'requisitions/currentDocument',
      hasNoteToSave: 'notes/hasNoteToSave'
    })
  },
  watch: {
    id (id) {
      if (this.edit && id) {
        const requisition = this.getRequisition(this.id)

        this.fillForm(requisition)
        this.$v.$reset()
      }
    },
    job (newJob, oldJob) {
      console.log('JOB WATCH TRIGGERED')
      console.log('newJob', newJob)
      console.log('oldJob', oldJob)
      if (!newJob) {
        this.shopDrawing = null
        return null
      }

      if (newJob && (newJob.id !== get('id', oldJob))) {
        console.log('NEW JOB IS DIFFERENT THAN OLD, UPDATING SHOP DRAWING LIST')
        const shopDrawingsList = this.shopDrawingsForJob(newJob)
        console.log('GOT NEW SHOP DRAWINGS: ', shopDrawingsList)
        this.shopDrawingsList = shopDrawingsList

        const currentShopDrawingIdMatch = shopDrawingsList.findIndex(shopDrawing => {
          return shopDrawing.id === get('id', this.shopDrawing) ||
            shopDrawing.id === this.shopDrawingID
        })

        if (currentShopDrawingIdMatch < 0 && this.shopDrawingsInit) this.shopDrawing = null

        return null
      }
    },
    shopDrawing (newSD, oldSD) {
      console.log('SD WATCH TRIGGERED')
      console.log('newSD', newSD)
      console.log('oldSD', oldSD)
      this.updateSubject(newSD, oldSD)

      if (!newSD) {
        this.number = null
        return null
      }

      if (newSD && (newSD.id !== get('id', oldSD))) {
        console.log('NEW SD IS DIFFERENT THAN OLD, UPDATING TO NUMBER & SUBJECT')
        this.refreshTONumber()

        const workReleasesList = this.workReleasesForShopDrawing(newSD)
        console.log('GOT NEW workReleasesList: ', workReleasesList)
        this.workReleasesList = workReleasesList

        const currentWorkReleaseIdMatch = workReleasesList.findIndex(workRelease => {
          return get('id', workRelease) === get('id', this.workRelease) ||
          get('id', workRelease) === this.workReleaseID
        })

        if (currentWorkReleaseIdMatch < 0 && this.workReleasesInit) this.workRelease = null

        return null
      }
    },
    shopDrawingsInit (newValue, oldValue) {
      console.log('SHOP_DRAWING INIT WATCH')
      if (newValue && !this.shopDrawingsList.length && this.job) {
        console.log('UPDATE SHOP DRAWING LIST')
        this.shopDrawingsList = this.shopDrawingsForJob(this.job)
      }
    },
    workRelease (newValue, oldValue) {
      console.log('workRelease WATCH TRIGGERED')
      console.log('newValue', newValue)
      console.log('oldValue', oldValue)
      this.updateSubject(oldValue, oldValue)
    },
    workReleasesInit (newValue, oldValue) {
      console.log('workReleasesInit WATCH')
      if (newValue && !this.workReleasesList.length && this.workRelease) {
        console.log('UPDATE workReleases LIST')
        this.workReleasesList = this.workReleasesForShopDrawing(this.shopDrawing)
      }
    }
  },

  methods: {
    delayTouch ($v, timeout = 1000) {
      $v.$reset()
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v))
      }
      touchMap.set($v, setTimeout($v.$touch, timeout))
    },

    uniqueNumber () {
      // Make sure the TO number is unique
    },

    jobFilter (item, search) {
      if (!search) return true

      const searchLower = search.toLowerCase()
      if (item) {
        if (item.name.toLowerCase().includes(searchLower)) return true
        if (item.number.toString().toLowerCase().includes(searchLower)) return true

        return false
      }

      return true
    },

    onJobInput (event) {
      console.log('job Input event: ', event)
      this.$v.job.$touch()
    },
    onJobBlur (event) {
      this.$v.job.$touch()
    },

    onShopDrawingFocus (event) {
      // console.log('onShopDrawingFocus', event)
      this.$refs.shopDrawingInput.isMenuActive = true
    },

    onShopDrawingInput (shopDrawing) {
      console.log('shopDrawingInput event: ', shopDrawing)

      this.$v.shopDrawing.$touch()
    },
    onShopDrawingBlur (event) {
      this.$v.shopDrawing.$touch()
    },

    onWorkReleaseFocus (event) {
      // console.log('onShopDrawingFocus', event)
      this.$refs.workReleaseInput.isMenuActive = true
    },

    onWorkReleaseInput (shopDrawing) {
      console.log('workRelease event: ', shopDrawing)

      this.$v.workRelease.$touch()
    },
    onWorkReleaseBlur (event) {
      this.$v.workRelease.$touch()
    },

    onConfirmDelete () {
      this.deleteRequisition(this.requisition.id)
      this.cancel()
    },

    shopDrawingsFilter (item, search, itemText) {
      const jobID = get('id', this.job)

      if (!search) return item.jobID === jobID

      const searchLower = search.toLowerCase()
      if (item) {
        if (item.jobID !== jobID) return false

        if (item.number.toLowerCase().includes(searchLower)) return true
        if (item.subject.toString().toLowerCase().includes(searchLower)) return true

        return false
      }

      return true
    },
    workReleasesFilter (item, search, itemText) {
      const shopDrawingID = get('id', this.shopDrawing)
      if (!search) return item.shopDrawingID === shopDrawingID

      const searchLower = search.toLowerCase()
      if (item) {
        if (item.shopDrawingID !== shopDrawingID) return false

        if (item.number.toLowerCase().includes(searchLower)) return true
        if (item.subject.toString().toLowerCase().includes(searchLower)) return true

        return false
      }

      return true
    },

    refreshTONumber (event) {
      console.log('GET TO NUMBER', event)
      const shopDrawings = this.shopDrawingsForJob(this.job)
      const shopDrawing = shopDrawings.find(sd => sd.id === this.shopDrawing.id)
      console.log('shopDrawings: ', shopDrawings)
      console.log('shopDrawing: ', shopDrawing)
      const maxTONumber = has('maxTONumber')(shopDrawing) ? shopDrawing.maxTONumber : 0
      console.log('maxTONumber: ', maxTONumber)
      this.number = maxTONumber + 1
    },

    clearForm () {
      this.job = {}
      this.shopDrawing = {}
      this.shopDrawingsList = []

      this.number = ''
      this.subject = ''

      this.$v.$reset()
    },

    fillForm (requisition) {
      console.log('FILLFORM')
      console.log('requisition: ', requisition)
      this.requisition = cloneDeep(requisition)

      this.job = getOr(null, 'job', requisition)
      this.shopDrawing = getOr(null, 'shopDrawing', requisition)
      this.number = getOr(null, 'number', requisition)
      this.subject = getOr(null, 'subject', requisition)
      console.log('job: ', get('job', requisition))
      console.log('shopDrawing: ', get('shopDrawing', requisition))
      console.log('number: ', get('number', requisition))
      console.log('subject: ', get('subject', requisition))
    },

    fillFormFromQuery (query) {
      console.log('fillFormFromQuery')
      console.log('query: ', query)

      if (!query) return null

      if (query.relatedJob) {
        this.job = this.getJobByQuickbaseRID(query.relatedJob)
      }

      if (query.relatedShopDrawing) {
        this.shopDrawing = this.getShopDrawingByQuickbaseRID(query.relatedShopDrawing)
      }

      if (query.relatedWorkRelease) {
        this.workRelease = this.getWorkReleaseByQuickbaseRID(query.relatedWorkRelease)
      }
    },

    updateSubject (newSD, oldSD) {
      console.log('updateSubject')
      console.log('isEqual', isEqual)
      console.log('this.subject: ', this.subject)

      const newSubject = get('subject', newSD)
      const oldSubject = get('subject', oldSD)
      console.log('newSD.subject', newSubject)
      console.log('oldSD.subject', oldSubject)
      console.log('isEqual(this.subject, oldSubject)', isEqual(this.subject, oldSubject))

      if (!this.subject) {
        this.subject = newSubject
        return null
      }

      if (isEqual(this.subject, oldSubject)) {
        this.subject = newSubject
        return null
      }
    },

    save () {
      // If no changes have been made, treat as Cancel
      if (!(this.$v.$dirty || this.$v.$anyDirty) && !this.$store.getters['notes/newNote'].length) {
        console.log('FORM IS NOT DIRTY, IGNORE SAVE AND CANCEL')
        this.cancel()
      }

      this.$v.$touch()

      if (this.$v.$error || this.$v.$anyError) {
        console.log('TEHRES A FUCKING ERROR, STOP')
        return null
      } else {
        console.log('BEGIN SAVING REQUISITION')
        this.saving = true

        const requisition = {
          jobID: this.job.id,
          job: this.job,

          shopDrawingID: this.shopDrawing.id,
          shopDrawing: this.shopDrawing,

          number: toNumber(this.number),
          subject: this.subject
        }

        if (this.edit) {
          console.log('SAVING AN EDITED requisition', requisition)

          // Compare to Original
          let original = cloneDeep(pick(this.updateComparisonProperties)(this.requisition))
          let updated = cloneDeep(pick(this.updateComparisonProperties)(requisition))
          const hasNoteToSave = this.hasNoteToSave
          if (isEqual(original, updated) && !hasNoteToSave) {
            console.log('no changes were made, ignore save')
            this.cancel()
            return null
          }

          this.update({
            id: this.id,
            requisition
          }).then(() => this.addNewNote(this.id))
            .then(() => {
              console.log('SHOP DRAWING Update Successfully AND NOTES ADDED')
              this.saving = false
              this.clearForm()
              this.$v.$reset()

              if (this.mode === 'dialog') {
                this.$emit('done')
              } else if (this.mode === 'route') {
                this.$router.back()
              }
            })
        } else {
          console.log('SAVING NEW requisition')
          console.log('requisition', requisition)
          this.add(requisition)
            .then(docId => this.addNewNote(docId))
            .then(() => {
              console.log('THEN AFTER NOTES ADDED')
              this.saving = false
              this.clearForm()
              this.$v.$reset()

              if (this.mode === 'dialog') {
                this.$emit('done')
              } else if (this.mode === 'route') {
                console.log('router: ', this.$router)
                this.$router.back()
              }
            })
        }
      }
    },
    cancel () {
      this.clearForm()
      if (this.mode === 'dialog') {
        this.$emit('cancel')
      } else if (this.mode === 'route') {
        this.$router.back()
      }
    },

    ...mapActions({
      add: 'requisitions/add',
      update: 'requisitions/update',
      deleteRequisition: 'requisitions/deleteRequisition',
      addNewNote: 'notes/addNewNote',
      getJobs: 'jobs/get',
      getShopDrawings: 'shopDrawings/get',
      getWorkReleases: 'workReleases/get'
    })
  },
  async created () {
    console.log('REQUISITION FORM CREATED', this)

    await Promise.all([
      this.getJobs(),
      this.getShopDrawings(),
      this.getWorkReleases()
    ])

    if (this.edit && this.id) {
      const requisition = this.currentRequisition
      this.fillForm(requisition)
    } else if (this.$route.query) {
      this.fillFormFromQuery(this.$route.query)
    }
  },
  directives: {
    'select-width': {
      inserted (el, binding, vnode) {
        // Item Type Selects are flexboxes, so they are only as wide as they need to be
        // However, this creates an issue when a new Type Select is created and has much longer item names
        //  than the standard min-width
        // This directive calculates a minimum width for the menu based on the length of the select items
        const STRING_CHAR_WIDTH = 7
        const MIN_MENU_WIDTH = 150
        console.log('select-width')
        console.log('el: ', el)
        console.log('vnode: ', vnode)
        const MAX_MENU_WIDTH = el.parentElement.offsetWidth
        console.log('MAX_MENU_WIDTH', MAX_MENU_WIDTH)

        // Array of possible choices for the select
        const items = binding.value.items
        console.log('items', items)

        if (size(items) < 1) {
          el.style.minWidth = `${MIN_MENU_WIDTH}px`
          return
        }

        // Get the longest (largest) str of the items
        const itemValue = binding.value.itemValue
        const itemValueIsTemplate = binding.value.itemValueIsTemplate
        const longestItem = maxBy(item => {
          return itemValueIsTemplate
            ? template(itemValue)(item).length
            : item.length
        })(items)

        const longestItemLength = itemValueIsTemplate
          ? template(itemValue)(longestItem).length
          : longestItem.length

        // Calculate a new menu minWidth
        const minWidth = Math.max(longestItemLength * STRING_CHAR_WIDTH, MIN_MENU_WIDTH)
        const maxWidth = Math.min(minWidth, MAX_MENU_WIDTH)
        console.log('minWidth', minWidth)
        console.log('maxWidth', maxWidth)
        el.style.minWidth = `${maxWidth}px`
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus" scoped>
@import '~vuetify/src/stylus/settings/_colors.styl'

.theme--dark
  .number__prefix
    color: $shades.white

.theme--light
  .number__prefix
    color: $shades.black

</style>
